import { useCallback } from 'react';

import {
  FamilyTreeActionFn,
  FamilyTreeNodeConfig,
  UpdateNodeConfigAction,
} from '../types';
import { computeCascadingHiddenNodes } from '../utils/computeCascadingHiddenNodes';

export function useUpdateNodeConfigAction() {
  return useCallback<FamilyTreeActionFn<UpdateNodeConfigAction>>(
    (state, action, queueEffect) => {
      const currentConfig: FamilyTreeNodeConfig = state.nodeConfigs[
        action.personId
      ] ?? {
        showNotes: false,
        showTile: true,
      };

      // Update the config for this person
      const updatedNodeConfigs = {
        ...state.nodeConfigs,
        [action.personId]: {
          ...currentConfig,
          [action.key]: action.value,
        },
      };

      // If we're hiding someone (showTile: false), compute cascading hides
      if (action.key === 'showTile' && action.value === false) {
        if (!state.familyTreeData?.people) {
          return {
            ...state,
            nodeConfigs: updatedNodeConfigs,
          };
        }

        const persons = state.familyTreeData.people.map((p) => ({
          id: p.clientProfile.id,
          isPrimary: p.clientProfile.isPrimary,
          generation: p.generation,
        }));

        // Compute which additional people should be cascadingly hidden
        const cascadingHiddenPersonIds = computeCascadingHiddenNodes(
          state.nodes,
          state.edges,
          persons,
          updatedNodeConfigs
        );

        // Update nodeConfigs for all cascadingly hidden people
        cascadingHiddenPersonIds.forEach((personId) => {
          const config: FamilyTreeNodeConfig = updatedNodeConfigs[personId] ?? {
            showNotes: false,
            showTile: true,
          };
          updatedNodeConfigs[personId] = {
            ...config,
            showTile: false,
          };
        });
      }

      // If we're unhiding someone (showTile: true), clear cascading hides
      if (action.key === 'showTile' && action.value === true) {
        // Find all person IDs that are currently marked as hidden
        const hiddenPersonIds = Object.entries(updatedNodeConfigs)
          .filter(([, config]) => config.showTile === false)
          .map(([personId]) => personId);

        // For each hidden person, check if they should still be hidden
        // If they're not explicitly set to hidden (by user), unhide them
        const personsToUnhide = new Set<string>();

        if (state.familyTreeData?.people) {
          const persons = state.familyTreeData.people.map((p) => ({
            id: p.clientProfile.id,
            isPrimary: p.clientProfile.isPrimary,
            generation: p.generation,
          }));
          const standalonePersonIds = new Set(
            persons
              .filter((person) => person.generation === -99)
              .map((person) => person.id)
          );

          // Recompute cascading hides with the updated config
          const cascadingHiddenPersonIds = computeCascadingHiddenNodes(
            state.nodes,
            state.edges,
            persons,
            updatedNodeConfigs
          );

          // Anyone who was hidden but is not in the cascading set and wasn't explicitly hidden
          // (other than the person we just unhid) should also be unhidden
          hiddenPersonIds.forEach((personId) => {
            if (
              personId !== action.personId &&
              !cascadingHiddenPersonIds.has(personId) &&
              !standalonePersonIds.has(personId)
            ) {
              personsToUnhide.add(personId);
            }
          });

          // Unhide these people
          personsToUnhide.forEach((personId) => {
            const config: FamilyTreeNodeConfig = updatedNodeConfigs[
              personId
            ] ?? {
              showNotes: false,
              showTile: true,
            };
            updatedNodeConfigs[personId] = {
              ...config,
              showTile: true,
            };
          });
        }
      }

      // Queue save effects for all nodes that had their config updated
      Object.entries(updatedNodeConfigs).forEach(([personId, config]) => {
        const originalConfig = state.nodeConfigs[personId];
        // Only save if this config differs from the original
        if (
          !originalConfig ||
          originalConfig.showTile !== config.showTile ||
          originalConfig.showNotes !== config.showNotes
        ) {
          queueEffect({
            type: 'saveNodeConfigEffect',
            clientProfileId: personId,
            originalConfig: originalConfig ?? {
              showTile: true,
              showNotes: false,
            },
            config,
          });
        }
      });

      return {
        ...state,
        nodeConfigs: updatedNodeConfigs,
      };
    },
    []
  );
}
